import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  BILLER_BUTTON_TEXT,
  CLICK_SUCCESS,
  FIELDS,
  KINESIS_EVENT_TYPE,
} from 'features/biller/constants';
import { getTranslatedMarketingDiscountCoupon } from 'features/productGrowth/api';

import Box from 'components/Box';
import Button from 'components/clickables/Button';
import TextInput from 'components/forms/TextInput';
import Loader from 'components/Loader';
import Text from 'components/Text';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import { trackMonetizationCtaEventKinesis } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

import {
  selectAppliedDiscountError,
  selectAppliedDiscountPending,
} from '../selectors';
import { actions } from '../slice';
import { applyAdminDiscount, applyDiscount } from '../thunks';

const { cxEl } = cxHelpers('BillerDiscountField');

export const DiscountField = ({
  appliedDiscount,
  applyError,
  onApply,
  onAdminApply,
  onRemove,
  isApplying,
  numberOfLocations,
  forAdmin,
  billingCycle,
  subscriptionPlan,
  companyId,
  discountAutoApplied,
  maskedDiscountCode,
  setMaskedDiscountCode,
  setIsAmexCouponChecked,
  subscriptionPriceNumeric,
  selectedProductUuid,
}) => {
  const trackUxEvent = useTrackUx();
  const [discountCode, setDiscountCode] = useState('');
  const [autoApplied, setAutoApplied] = useState(discountAutoApplied);
  const [discountFieldExpanded, setDiscountFieldExpanded] = useState(false);

  const handleApplyDiscount = useCallback(() => {
    setAutoApplied(false);

    return getTranslatedMarketingDiscountCoupon({
      code: discountCode,
      planName: subscriptionPlan,
    }).then(res => {
      const translatedDiscountCode = res?.coupon;

      trackMonetizationCtaEventKinesis(KINESIS_EVENT_TYPE, CLICK_SUCCESS, {
        actionDetails: 'apply_discount_code',
        discountCode: translatedDiscountCode,
      });
      trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
        button_text: BILLER_BUTTON_TEXT.COUPON_APPLY,
        eventCategory: EVENT_CATEGORIES.ORDER_SUMMARY_MODAL,
      });

      if (forAdmin) {
        onAdminApply({
          discountCode: translatedDiscountCode,
          planName: subscriptionPlan,
          billingCycle,
          companyId,
        });
      } else {
        onApply({
          discountCode: translatedDiscountCode,
          planName: subscriptionPlan,
          billingCycle,
          productUuid: selectedProductUuid,
        });
      }

      setMaskedDiscountCode?.(discountCode);
    });
  }, [
    onApply,
    forAdmin,
    companyId,
    discountCode,
    trackUxEvent,
    onAdminApply,
    billingCycle,
    subscriptionPlan,
    setMaskedDiscountCode,
    selectedProductUuid,
  ]);

  const handleFieldFocus = useCallback(() => {
    trackUxEvent(EVENT_ACTIONS.FIELD_FOCUSED, TRACK_ACTION_TYPES.FOCUS, {
      field: FIELDS.SUPPORT_CODE,
      eventCategory: EVENT_CATEGORIES.ORDER_SUMMARY_MODAL,
    });
  }, [trackUxEvent]);

  const handleClearCoupon = useCallback(() => {
    setDiscountCode('');
    setIsAmexCouponChecked(false);
    onRemove();
  }, [setIsAmexCouponChecked, onRemove]);

  useEffect(() => {
    if (applyError) {
      trackUxEvent(EVENT_ACTIONS.ERROR_MESSAGE_SHOWN, TRACK_ACTION_TYPES.VIEW, {
        autoApplied,
        field: FIELDS.SUPPORT_CODE,
        eventCategory: EVENT_CATEGORIES.ORDER_SUMMARY_MODAL,
        error: applyError,
      });
    }
  }, [applyError, autoApplied, trackUxEvent]);

  useEffect(
    () => () => {
      handleClearCoupon();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const discountedAmount = useMemo(() => {
    if (!appliedDiscount) {
      return 0;
    } else if (appliedDiscount.get('amount')) {
      return appliedDiscount.get('amount');
    } else if (appliedDiscount.get('percent_off')) {
      return (
        (appliedDiscount.get('percent_off') / 100) * subscriptionPriceNumeric
      );
    }
  }, [appliedDiscount, subscriptionPriceNumeric]);

  return (
    <Box>
      {!autoApplied &&
      !discountAutoApplied &&
      !appliedDiscount &&
      !discountFieldExpanded &&
      !forAdmin ? (
        <Box>
          <Box
            mt={10}
            className={cxEl('discount-block')}
            onClick={setDiscountFieldExpanded}
            pointer
          >
            <Text
              fs14
              fw700
              blueMedium
              i18n="billing.modal.discount.have_one"
            />
          </Box>
        </Box>
      ) : (
        <Box mt={10} className={cxEl('discount-block')}>
          {appliedDiscount && (
            <Box row spacebetween mb12>
              <Text fs14>
                {toI18n('billing.modal.discount.desc')}
                {numberOfLocations > 1 && ` x${numberOfLocations}`}
              </Text>
              <Text fs16>
                <NumberFormat
                  value={-discountedAmount}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                />
                {numberOfLocations > 1 && '/ea'}
              </Text>
            </Box>
          )}
          <Box>
            {appliedDiscount ? (
              <Box>
                {!autoApplied && (
                  <Box vcenter b p={10} row spacebetween w={171} bradius={3}>
                    <Text fw600 fs16>
                      {maskedDiscountCode}
                    </Text>
                    <Button
                      iconColor="red"
                      icon="closeSimple"
                      theme="link"
                      onClick={handleClearCoupon}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Loader isLoading={isApplying}>
                <Box row spacebetween>
                  <TextInput
                    className="mr8"
                    inline
                    placeholder={toI18n('billing.modal.discount.enter')}
                    value={discountCode}
                    onChange={setDiscountCode}
                    onFocus={handleFieldFocus}
                    disabled={appliedDiscount}
                    size="narrow"
                    valueOnChange
                  />
                  <Button
                    theme="link"
                    size="mini"
                    i18n="actions.apply"
                    onClick={handleApplyDiscount}
                    disabled={!discountCode}
                  />
                </Box>

                <Box>
                  <Text red fs12>
                    {applyError}
                  </Text>
                </Box>
              </Loader>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

DiscountField.propTypes = {
  onApply: PropTypes.func,
  onAdminApply: PropTypes.func,
  onRemove: PropTypes.func,
  isApplying: PropTypes.bool,
  applyError: PropTypes.string,
  discountAmount: PropTypes.number,
  appliedDiscount: ImmutablePropTypes.map,
  numberOfLocations: PropTypes.number,
  forAdmin: PropTypes.bool,
  discountAutoApplied: PropTypes.bool,
  billingCycle: PropTypes.string,
  subscriptionPlan: PropTypes.string,
  companyId: PropTypes.number,
  maskedDiscountCode: PropTypes.string,
  setMaskedDiscountCode: PropTypes.func,
  setIsAmexCouponChecked: PropTypes.func.isRequired,
  subscriptionPriceNumeric: PropTypes.number,
  selectedProductUuid: PropTypes.string,
};

export default connect(
  state => ({
    isApplying: selectAppliedDiscountPending(state),
    applyError: selectAppliedDiscountError(state),
  }),
  {
    onApply: applyDiscount,
    onAdminApply: applyAdminDiscount,
    onRemove: actions.removeDiscount,
  }
)(DiscountField);
